<!-- IKE and IKZE: contributions vs limits, PIT deduction, the tax you don't pay. -->
<script lang="ts">
  import * as E from "../core/engine";
  import * as X from "../core/analysis";
  import { app, need } from "../lib/state.svelte";
  import { fmtNum, parseNum } from "../lib/format";
  import { baseOption, compactMoney, palette, series, tipMoney, tooltipRows, valueAxis, type Opt, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";

  type Kind = "IKE" | "IKZE";
  type Limits = Record<string, { ike: number; ikze: number; ikzeSelf: number } | undefined>;

  const m = $derived(need(app.model));
  const cfg = $derived(need(app.data).config);
  const rc = $derived(X.retirementContrib(m, cfg));
  const self = $derived(cfg.ikzeSelfEmployed !== false);
  const pitRate = $derived(app.ui.ike?.pit ?? 0.19);
  const yNow = $derived(m.today.slice(0, 4));
  const accName = (a: string) => cfg.accounts?.[a]?.name || a;
  const lim = (y: string) => (cfg.limits as Limits | undefined)?.[y] || X.RETIREMENT_LIMITS[+y] || null;
  const limOf = (y: string, k: Kind): number | null => { const l = lim(y); return l ? (k === "IKE" ? l.ike : self ? l.ikzeSelf : l.ikze) : null; };
  const dep = (y: string, k: Kind) => rc.byYear[y]?.[k]?.dep || 0;
  const daysLeft = $derived(E.diffDays(m.today, `${yNow}-12-31`));
  const e = $derived(m.idx(m.today));
  // tax benefit: gain on IKE/IKZE accounts × Belka rate (the tax you don't pay)
  const ex = $derived.by(() => {
    const Aex = E.aggregate(m, [...rc.accs.IKE, ...rc.accs.IKZE], false);
    return { valEx: Aex.V[e], gainEx: Aex.V[e] - Aex.invested[e] };
  });
  const vIKZE = $derived(rc.accs.IKZE.length ? E.aggregate(m, rc.accs.IKZE, false).V[e] : 0);
  const kinds = $derived((["IKE", "IKZE"] as const).filter((k) => rc.accs[k].length));
  const years = $derived([...new Set([...Object.keys(rc.byYear), yNow])].sort().reverse());

  // after a change the field is re-rendered (as after redrawing the view): a fresh element with the normalised value
  let gen = $state(0);
  function setPit(ev: Event & { currentTarget: HTMLInputElement }) {
    const el = ev.currentTarget;
    app.ui.ike = { ...(app.ui.ike ?? {}), pit: parseNum(el.value, 19) / 100 };
    gen++;
  }

  const ikeOpt = $derived.by(() => {
    const p = palette();
    const ys = years.slice().reverse();
    const ser: Opt[] = [];
    kinds.forEach((k, j) => {
      const c = series(j);
      ser.push({ name: `${k}: wpłaty`, type: "bar", barMaxWidth: 26, barGap: "20%", data: ys.map((y) => +dep(y, k).toFixed(2)), itemStyle: { color: c, borderRadius: [3, 3, 0, 0] } });
      ser.push({ name: `${k}: limit`, type: "line", step: "middle", data: ys.map((y) => limOf(y, k)), symbol: "none", lineStyle: { color: c, type: "dashed", width: 1.5 }, itemStyle: { color: c } });
    });
    return {
      ...baseOption(p), tooltip: { ...baseOption(p).tooltip, axisPointer: { type: "shadow" }, formatter: (ps: TipParam) => tooltipRows(ps, (v) => tipMoney(v, 2)) },
      xAxis: { type: "category", data: ys, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } }, axisLabel: { color: p.muted } },
      yAxis: valueAxis(compactMoney, p), series: ser,
    };
  });
</script>

{#if !rc.accs.IKE.length && !rc.accs.IKZE.length}
  <ViewHead title="IKE i IKZE" />
  <div class="notice">Nie rozpoznano rachunków IKE ani IKZE. Oznacz je w <a href="#/settings">Ustawieniach</a> (kolumna „Typ”).</div>
{:else}
  <ViewHead title="IKE i IKZE" sub="limity wpłat {yNow}: IKE {fmtNum(limOf(yNow, 'IKE'), 2)} zł · IKZE {fmtNum(limOf(yNow, 'IKZE'), 2)} zł{self ? ' (prowadzący działalność)' : ''}" />
  <div class="kpis">
    {#each kinds as k (k)}
      {@const l = limOf(yNow, k)}
      {@const d = dep(yNow, k)}
      {@const rest = Math.max(0, (l || 0) - d)}
      <Kpi label="{k}: wpłaty w {yNow}">{#snippet value()}<Money v={d} />{/snippet}{#snippet sub()}{#if l}{fmtNum((d / l) * 100, 0)}% limitu · zostało <b><Money v={rest} /></b>{:else}brak limitu dla roku{/if}{rest > 0 && daysLeft < 92 ? ` · ${daysLeft} dni do końca roku` : ""}{/snippet}</Kpi>
    {/each}
    {#if rc.accs.IKZE.length}
      <Kpi label="Ulga PIT z IKZE za {yNow}">{#snippet value()}<Money v={dep(yNow, "IKZE") * pitRate} />{/snippet}{#snippet sub()}wpłaty × {fmtNum(pitRate * 100, 0)}% · dopełnienie limitu dałoby jeszcze <Money v={Math.max(0, (limOf(yNow, "IKZE") || 0) - dep(yNow, "IKZE")) * pitRate} />{/snippet}</Kpi>
    {/if}
    <Kpi label="Belka, której nie płacisz">{#snippet value()}<Money v={Math.max(0, ex.gainEx) * m.taxRate} />{/snippet}{#snippet sub()}{fmtNum(m.taxRate * 100, 0)}% od zysku <Signed v={ex.gainEx} /> na IKE/IKZE (wartość <Money v={ex.valEx} />){/snippet}</Kpi>
    {#if rc.accs.IKZE.length}
      <Kpi label="IKZE: podatek przy wypłacie" sub="10% zryczałtowanego podatku od całej kwoty po 65. roku życia">{#snippet value()}<Money v={vIKZE * 0.1} />{/snippet}</Kpi>
    {/if}
  </div>
  <Card title="Wpłaty na tle limitów" hint="limity: 3-krotność (IKE) i 1,2-krotność (IKZE; 1,8-krotność dla prowadzących działalność) prognozowanego przeciętnego wynagrodzenia">
    {#snippet extra()}<div class="row small">Stawka PIT do ulgi IKZE: {#key gen}<input type="number" class="num" id="ikePit" value={fmtNum(pitRate * 100, 0)} step="1" style="width:60px" onchange={setPit} />{/key} %</div>{/snippet}
    <Chart id="chIke" option={ikeOpt} />
  </Card>
  <Card title="Rok po roku" hint="rachunki: {kinds.map((k) => `${k} — ${rc.accs[k].map((a) => accName(a)).join(', ')}`).join(' · ')}">
    <div class="table-wrap"><table><thead><tr><th class="l">Rok</th>{#each kinds as k (k)}<th>{k}: wpłaty</th><th>Limit</th><th>Wykorzystanie</th>{/each}{#if rc.accs.IKZE.length}<th>Ulga PIT z IKZE</th>{/if}</tr></thead><tbody>
      {#each years as y (y)}
        <tr><td class="l"><b>{y}</b></td>{#each kinds as k (k)}{@const l = limOf(y, k)}{@const d = dep(y, k)}<td><Money v={d} /></td><td class="muted">{#if l}<Money v={l} />{:else}–{/if}</td><td>{#if l}{fmtNum((d / l) * 100, 0)}%<span class="bar-inline" style="width:{Math.round(Math.min(1, d / l) * 50)}px"></span>{:else}–{/if}</td>{/each}
          {#if rc.accs.IKZE.length}<td><Money v={dep(y, "IKZE") * pitRate} /></td>{/if}</tr>
      {/each}
    </tbody></table></div>
  </Card>
  <p class="small muted">Wpłaty = operacje „wpłata” na rachunkach IKE/IKZE. Na IKE obligacji wpłatą jest każdy zakup obligacji — jeśli kupowałeś je z pieniędzy z wykupu lub kuponów, które zostały na IKE, faktyczne wykorzystanie limitu jest mniejsze. Ulga IKZE: wpłatę odliczasz od dochodu w zeznaniu rocznym; oszczędność zależy od Twojej stawki podatku. Limity z lat ubiegłych możesz nadpisać w data/config.json (klucz <code>limits</code>).</p>
{/if}
